import { forwardRef, useId } from 'react'
import type { ReactNode } from 'react'
import { cn } from './cn'

export interface SwitchProps {
  checked?: boolean
  onChange?: (next: boolean) => void
  label?: ReactNode
  hint?: ReactNode
  error?: ReactNode
  disabled?: boolean
  id?: string
  className?: string
}

/** Toggle switch — accessible role="switch"; on = brand accent. forwardRef for RHF. */
export const Switch = forwardRef<HTMLButtonElement, SwitchProps>(function Switch(
  { checked = false, onChange, label, hint, error, disabled = false, id, className = '' },
  ref
) {
  const gid = useId()
  const labelId = `${gid}-label`
  const descId = hint ? `${gid}-hint` : undefined
  const errorId = error ? `${gid}-error` : undefined
  const describedBy = [errorId, descId].filter(Boolean).join(' ') || undefined
  const toggle = () => { if (!disabled) onChange?.(!checked) }
  return (
    <span className={cn('inline-flex items-start gap-3', disabled && 'opacity-55', className)}>
      <button
        ref={ref}
        type="button"
        role="switch"
        id={id}
        aria-checked={checked}
        aria-labelledby={label ? labelId : undefined}
        aria-describedby={describedBy}
        aria-invalid={error ? true : undefined}
        disabled={disabled}
        onClick={toggle}
        className={cn(
          'relative inline-flex h-6 w-11 shrink-0 items-center rounded-pill transition-colors focus-visible:outline-none focus-visible:shadow-focus disabled:cursor-not-allowed',
          checked ? 'bg-vjal-500' : 'bg-stone'
        )}
      >
        <span className={cn('inline-block h-5 w-5 rounded-full bg-white shadow-1 transition-transform', checked ? 'translate-x-[22px]' : 'translate-x-0.5')} />
      </button>
      {(label || hint || error) && (
        <span className="flex flex-col">
          {label && (
            <span id={labelId} className={cn('text-ink-body select-none', !disabled && 'cursor-pointer')} onClick={disabled ? undefined : toggle}>
              {label}
            </span>
          )}
          {hint && !error && <span id={descId} className="text-xs text-ink-muted">{hint}</span>}
          {error && <span id={errorId} role="alert" className="text-sm text-err-ink">{error}</span>}
        </span>
      )}
    </span>
  )
})
